import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import "../../../constants/index.js";
import useThemePresetClasses from "../../../hooks/useThemePresetClasses.js";
import { cn } from "../../../utils/cn.js";
import { delay } from "../../../utils/misc.js";
import { useId, useCallback, useState } from "react";
import { Listbox as Mo, ListboxButton as Nt, ListboxOptions as Gt, ListboxOption as Vt } from "../../../external/@headlessui/react/dist/components/listbox/listbox.js";
import SvgArrowDown from "../../../assets/vuesax/arrow-down.svg.js";
import Button from "../Button/Button.js";
import Drawer from "../../utils/Drawer/Drawer.js";
import DrawerTitle from "../../utils/DrawerTitle/DrawerTitle.js";
import SelectOptions from "../../blocks/SelectOptions/SelectOptions.js";
function Select({
  label,
  options,
  renderValue,
  renderOption,
  className,
  classes,
  helperText,
  error,
  disabled = false,
  placeholder,
  icon,
  dropdownGap = 32,
  size = "md",
  value,
  onChange,
  optionIconPlaceholder,
  drawerTitle,
  variant = "dropdown",
  onSelected,
  selected
}) {
  var _a, _b;
  const { themeClasses } = useThemePresetClasses("Select", classes, className, {
    modifier: size
  });
  const selectId = useId();
  const displayValue = value ? (_a = options.find(({ id: v }) => v === value)) == null ? void 0 : _a.name : placeholder ?? "";
  const displayIcon = value ? ((_b = options.find(({ id: v }) => v === value)) == null ? void 0 : _b.icon) ?? icon : icon;
  const updateContainerStyles = useCallback(() => {
    if (selectId && !!dropdownGap) {
      delay(50).then(() => {
        const optionsContainer = document.getElementById(selectId);
        if (!optionsContainer) return;
        const optionsContainerStyle = getComputedStyle(optionsContainer);
        const maxHeight = parseInt(optionsContainerStyle.maxHeight.replace("px", ""));
        optionsContainer.style.setProperty("--anchor-max-height", `${maxHeight - dropdownGap}px`);
      });
    }
  }, [dropdownGap, selectId]);
  const handleOnSelectOption = (option) => {
    onSelected == null ? void 0 : onSelected(option);
    setIsOpenDrawer(false);
  };
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const handleOpenDrawer = useCallback(() => {
    setIsOpenDrawer((prev) => !prev);
  }, []);
  const renderSelectWithVariant = () => {
    if (variant === "dropdown") {
      return /* @__PURE__ */ jsxs("label", { className: cn(themeClasses == null ? void 0 : themeClasses.root), children: [
        typeof label === "string" ? /* @__PURE__ */ jsx("span", { className: cn(themeClasses == null ? void 0 : themeClasses.label), children: label }) : label,
        /* @__PURE__ */ jsxs(
          Mo,
          {
            value,
            onChange,
            disabled,
            children: [
              /* @__PURE__ */ jsx(
                Nt,
                {
                  onClick: updateContainerStyles,
                  className: cn(themeClasses == null ? void 0 : themeClasses.select, error ? "!border-rose-500 focus:!border-rose-500" : ""),
                  children: /* @__PURE__ */ jsxs("div", { className: cn(themeClasses == null ? void 0 : themeClasses.selectContentContainer), children: [
                    /* @__PURE__ */ jsxs("div", { className: cn(themeClasses == null ? void 0 : themeClasses.selectContent), children: [
                      !!displayIcon && /* @__PURE__ */ jsx("div", { className: cn("max-h-8 max-w-8"), children: displayIcon }),
                      renderValue && value ? renderValue(value) : /* @__PURE__ */ jsx("div", { className: cn("truncate", themeClasses == null ? void 0 : themeClasses.placeHolder), children: displayValue })
                    ] }),
                    /* @__PURE__ */ jsx(SvgArrowDown, { className: cn("truncate", themeClasses == null ? void 0 : themeClasses.iconDropdown) })
                  ] })
                }
              ),
              /* @__PURE__ */ jsx(
                Gt,
                {
                  anchor: "bottom",
                  transition: true,
                  className: cn(themeClasses == null ? void 0 : themeClasses.optionsContainer),
                  id: selectId,
                  children: options.map((o) => /* @__PURE__ */ jsx(
                    Vt,
                    {
                      value: o.id,
                      className: cn(themeClasses == null ? void 0 : themeClasses.option, o.disabled ? themeClasses == null ? void 0 : themeClasses.disabledOption : ""),
                      disabled: o.disabled,
                      children: ({ focus, selected: selected2 }) => /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: cn({
                            [(themeClasses == null ? void 0 : themeClasses.selectedOption) ?? ""]: selected2
                          }),
                          children: renderOption ? renderOption(o) : /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-x-2", children: [
                            o.icon ?? optionIconPlaceholder,
                            o.name
                          ] })
                        }
                      )
                    },
                    o.id
                  ))
                }
              )
            ]
          }
        ),
        !!helperText && /* @__PURE__ */ jsx("div", { className: cn(themeClasses == null ? void 0 : themeClasses.helperText, error ? themeClasses == null ? void 0 : themeClasses.errorText : ""), children: helperText })
      ] });
    }
    if (variant === "drawer") {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex w-full items-center gap-1", children: /* @__PURE__ */ jsxs(
          Button,
          {
            onClick: handleOpenDrawer,
            variant: "contained",
            icon: /* @__PURE__ */ jsx(SvgArrowDown, { className: cn("truncate", themeClasses == null ? void 0 : themeClasses.iconDropdown) }),
            iconPosition: "right",
            size: "lg",
            classes: {
              "label&": "text-base font-normal whitespace-nowrap text-content-base w-full justify-between px-3"
            },
            children: [
              (selected == null ? void 0 : selected.name) ?? placeholder,
              (selected == null ? void 0 : selected.imgUrl) && /* @__PURE__ */ jsx(
                "img",
                {
                  src: selected.imgUrl,
                  alt: "provider",
                  className: "h-5 w-auto"
                }
              )
            ]
          }
        ) }),
        /* @__PURE__ */ jsxs(
          Drawer,
          {
            open: isOpenDrawer,
            onClose: () => setIsOpenDrawer(false),
            placement: "bottom",
            children: [
              /* @__PURE__ */ jsx(DrawerTitle, { title: drawerTitle ? drawerTitle : placeholder }),
              /* @__PURE__ */ jsx(
                SelectOptions,
                {
                  className: "mx-8 mb-6",
                  options,
                  selectedOption: selected,
                  handleOnSelect: handleOnSelectOption
                }
              )
            ]
          }
        )
      ] });
    }
  };
  return /* @__PURE__ */ jsx(Fragment, { children: renderSelectWithVariant() });
}
export {
  Select as default
};
